import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthService } from 'src/app/core/services/auth.service';
import { AccountServiceInterface } from './account.service.interface';
import { ApiService } from './api.service';
import { UserApiService } from './user-api.service';

/**
 * This service is used to get and update user information such as avatar, password, etc.
 */
@Injectable({
  providedIn: 'root',
})
export class AccountService implements AccountServiceInterface {
  public avatar$ = new BehaviorSubject<string>('');
  public phoneNumberUpdated$ = new Subject<string>();

  constructor(
    private userApi: UserApiService,
    private auth: AuthService,
    private api: ApiService
  ) {}

  /**
   * Fetch avatar and store image data in base64 format.
   * The avatar needs to be accessed any time from TopBar and Account page.
   *
   * @param force If true, it forces to refetch data from backend.
   */
  fetchAvatar(force?: boolean) {
    this.api.getAvatar({ force }).subscribe(
      (data: string) => {
        if (data) {
          this.avatar$.next(`data:image/png;base64,${data}`);
        } else {
          this.avatar$.next('');
        }
      },
      () => {
        this.avatar$.next('');
      }
    );
  }

  /**
   * Submit avatar base64 string to the backend.
   *
   * @param base64Img Avatar image data (base64 string. "data:image/png;base64," is removed before submission)
   */
  saveAvatar(base64Img: string) {
    return this.api.saveAvatar(base64Img.slice(22)).pipe(
      tap(() => {
        this.fetchAvatar(true);
      })
    );
  }

  /**
   * Delete avatar on the backend
   */
  deleteAvatar() {
    return this.api.deleteAvatar().pipe(
      tap(() => {
        this.fetchAvatar(true);
      })
    );
  }

  /**
   * Update account password.
   */
  updatePassword(oldPassword: string, newPassword: string) {
    return this.userApi.updateUserInfo('UpdatePassword', {
      CurrentPassword: oldPassword,
      NewPassword: newPassword,
    });
  }

  /**
   * Update account details. You can only update first name and last name with this API.
   */
  updateDetails(details: { FirstName: string; LastName: string }) {
    return this.userApi.updateUserInfo('UpdateName', details);
  }

  /**
   * This will generate verification code to be submitted when updating phone number or email.
   *
   * When you update email, Username must be present in data.
   * When you update phone number, PhoneNumber must be present in data.
   */
  generateCode(data: { UserName?: string; PhoneNumber?: string }) {
    return this.userApi.generateCode(data);
  }

  /**
   * This will submit the code you received on your email or phone along with the generated code with "generateCode" api
   * @param {Object} data
   * @param {String} data.LongCode The code you received on your email or phone
   * @param {String} data.ShortCode The code you retried using "generatedCode"
   */
  verifyCode(data: { LongCode: string; ShortCode: string }) {
    return this.userApi.verifyCode(data);
  }

  /**
   * Send a request to update phone number. This will send an SMS code to your phone.
   */
  updatePhoneNumber(phoneNumber: string) {
    return this.userApi.updateUserInfo('UpdatePhoneNumber', {
      PhoneNumber: phoneNumber,
    });
  }

  /**
   * Send a request to update email. This will send a code to your email.
   */
  updateUserName(email: string) {
    return this.userApi.updateUserInfo('UpdateUsername', {
      UserName: email,
      CompanyID: this.auth.companyID,
    });
  }
}
